export const analyticsInputFragment = gql`
  fragment AnalyticsInputFragment on AnalyticsInput {
    id
    period
    marketingInfluencers
    marketingEmail
    marketingOther
    shippingCost
    transactionFee
    transactionFlatFee
    cogs
    noExternalCogs
    operatingExpense
    interestExpense
  }
`;

export const analyticsInputUseMonthlyFragment = gql`
  fragment AnalyticsInputUseMonthlyFragment on AnalyticsInputUseMonthly {
    marketingInfluencers
    marketingEmail
    marketingOther
    shippingCost
    transactionFee
    transactionFlatFee
    cogs
    operatingExpense
    interestExpense
  }
`;

export const analyticsInputsResponseFragment = gql`
  fragment AnalyticsInputsResponseFragment on AnalyticsInputsResponse {
    inputs {
      ...AnalyticsInputFragment
    }
    useMonthly {
      ...AnalyticsInputUseMonthlyFragment
    }
  }

  ${analyticsInputFragment}
  ${analyticsInputUseMonthlyFragment}
`;

export const ANALYTICS_INPUTS_QUERY = gql`
  query AnalyticsInputsQuery($integrationId: String!, $periods: [String!]!) {
    analyticsInputs(integrationId: $integrationId, periods: $periods) {
      ...AnalyticsInputsResponseFragment
    }
  }

  ${analyticsInputsResponseFragment}
`;

export const UPSERT_ANALYTICS_INPUTS_MUTATION = gql`
  mutation UpsertAnalyticsInputMutation(
    $inputs: [UpsertAnalyticsInput!]
    $useMonthly: UpsertAnalyticsInputUseMonthly
    $integrationId: String!
  ) {
    upsertAnalyticsInputs(
      inputs: $inputs
      useMonthly: $useMonthly
      integrationId: $integrationId
    )
  }
`;

// Unit Economics
export const unitEconomicsFragment = gql`
  fragment UnitEconomicsFragment on UnitEconomics {
    period
    returningCustomer
    orders
    aur
    upt
    grossRetail
    dr
    rr
    shippingCollected
    netSpend
    aov
    cogs
    shippingPaid
    transactionFee
    grossProfit
    directMarketing
    profitContribution
    netIncome
  }
`;

export const unitEconomicsResponseFragment = gql`
  fragment UnitEconomicsResponseFragment on UnitEconomicsResponse {
    data {
      ...UnitEconomicsFragment
    }
    comparisonData {
      ...UnitEconomicsFragment
    }
    resolution
    minDate
  }

  ${unitEconomicsFragment}
`;

export const UNIT_ECONOMICS_QUERY = gql`
  query UnitEconomicsQuery(
    $integrationId: String!
    $dateFrom: Date!
    $dateTo: Date!
    $reportingInterval: ReportingInterval
    $compareFrom: Date
    $compareTo: Date
  ) {
    unitEconomics(
      integrationId: $integrationId
      dateFrom: $dateFrom
      dateTo: $dateTo
      reportingInterval: $reportingInterval
      compareFrom: $compareFrom
      compareTo: $compareTo
    ) {
      ...UnitEconomicsResponseFragment
    }
  }

  ${unitEconomicsResponseFragment}
`;

// Normalized P&L
export const normalizedPLFragment = gql`
  fragment NormalizedPLFragment on NormalizedPL {
    period
    grossSales
    discounts
    refunds
    shipping
    netRevenue
    cogs
    shippingCost
    transactionFee
    grossProfit
    marketingSpend
    metaMarketingSpend
    googleMarketingSpend
    otherMarketingSpend
    profitContribution
    operatingExpense
    ebitda
    interestExpense
    netIncome
  }
`;

export const normalizedPLResponseFragment = gql`
  fragment NormalizedPLResponseFragment on NormalizedPLResponse {
    data {
      ...NormalizedPLFragment
    }
    comparisonData {
      ...NormalizedPLFragment
    }
    resolution
    minDate
  }

  ${normalizedPLFragment}
`;

export const NORMALIZED_PL_QUERY = gql`
  query NormalizedPLQuery(
    $integrationId: String!
    $dateFrom: Date!
    $dateTo: Date!
    $reportingInterval: ReportingInterval
    $compareFrom: Date
    $compareTo: Date
  ) {
    normalizedPL(
      integrationId: $integrationId
      dateFrom: $dateFrom
      dateTo: $dateTo
      reportingInterval: $reportingInterval
      compareFrom: $compareFrom
      compareTo: $compareTo
    ) {
      ...NormalizedPLResponseFragment
    }
  }

  ${normalizedPLResponseFragment}
`;

const cohortAnalyticsFragment = gql`
  fragment CohortMetricsFragment on CohortMetrics {
    ltv30
    ltv90
    cacPayback
    metrics
  }

  fragment CohortFragment on CohortAnalytics {
    period
    count
    nCAC
    aof
    firstNS
    refund
    churn
    repeat30
    repeatDays
    threpeat30
    repeatCustomerRate
    profitMetrics {
      ...CohortMetricsFragment
    }
    revenueMetrics {
      ...CohortMetricsFragment
    }
  }
`;

export const COHORT_ANALYTICS_QUERY = gql`
  query CohortsQuery(
    $integrationId: String!
    $dateFrom: Date!
    $dateTo: Date!
    $compareFrom: Date
    $compareTo: Date
    $cohortMetric: CohortMetric
  ) {
    cohortAnalytics(
      integrationId: $integrationId
      dateFrom: $dateFrom
      dateTo: $dateTo
      compareFrom: $compareFrom
      compareTo: $compareTo
      cohortMetric: $cohortMetric
    ) {
      data {
        ...CohortFragment
      }
      comparisonData {
        ...CohortFragment
      }
      minDate
    }
  }

  ${cohortAnalyticsFragment}
`;

const aovAnalyticsFragment = gql`
  fragment AovAnalyticsFragment on AovAnalytics {
    buckets
    orders
    cumulativeOrders
    shipping
    cumulativeShipping
    collectionRate
    upt
    distribution
    cumulativeDistribution
    profit
    profitPerOrder
    revenue
    revenuePerOrder
    repeatCustomers
    repeatCustomerRate
  }
`;

export const AOV_ANALYTICS_QUERY = gql`
  query AovQuery(
    $integrationId: String!
    $dateFrom: Date!
    $dateTo: Date!
    $compareFrom: Date
    $compareTo: Date
    $returningCustomer: Boolean
    $increment: Float
  ) {
    aovAnalytics(
      integrationId: $integrationId
      dateFrom: $dateFrom
      dateTo: $dateTo
      compareFrom: $compareFrom
      compareTo: $compareTo
      returningCustomer: $returningCustomer
      increment: $increment
    ) {
      data {
        ...AovAnalyticsFragment
      }
      comparisonData {
        ...AovAnalyticsFragment
      }
      minDate
      suggestedIncrement
      suggestedFreeShippingThreshold
    }
  }

  ${aovAnalyticsFragment}
`;

const breakEvenAnalysisFragment = gql`
  fragment BreakEvenAnalysisFragment on BreakEvenAnalysis {
    orders
    aov
    grossMargin
    mer
    operatingExpense
  }
`;

export const BREAK_EVEN_ANALYSIS_QUERY = gql`
  query BreakEvenAnalysisQuery($integrationId: String!) {
    breakEvenAnalysis(integrationId: $integrationId) {
      data {
        ...BreakEvenAnalysisFragment
      }
    }
  }

  ${breakEvenAnalysisFragment}
`;
